/**
 * To avoid race conditions, this file gets imported here
 * app/public/wp-content/mu-plugins/wp-common-plugin/src/Gutenberg/Modules/Listing/scripts/view.js
 */
import RangeSlider from 'nouislider';
import { Facet, Event, isRangeFacet, isSingularFacet, getTagTypeName, isEnumerableFacet } from './product-facets.js';
import '../../../Components/FilterPanel/filter-panel-view.js';
import './filter-modal.js';
import * as utils from './filter-utils.js';
import './collapse-options.js';

( function() {
	'use strict';

	const INTERNAL_DATA_KEY = '__colibriFilterData';

	function toDatasetKey( key ) {
		return key.replace( /-([a-z])/g, ( _, char ) => char.toUpperCase() );
	}

	class DollarCollection {
		constructor( elements, prevObject = null ) {
			this.elements = elements || [];
			this.prevObject = prevObject;
		}

		get length() {
			return this.elements.length;
		}

		get( index ) {
			return this.elements[ index ];
		}

		first() {
			return new DollarCollection( this.elements.length ? [ this.elements[ 0 ] ] : [], this );
		}

		each( callback ) {
			this.elements.forEach( ( el, idx ) => callback.call( el, idx, el ) );
			return this;
		}

		find( selector ) {
			const found = this.elements.flatMap( ( el ) => Array.from( el.querySelectorAll( selector ) ) );
			return new DollarCollection( found, this );
		}

		closest( selector ) {
			const found = this.elements
				.map( ( el ) => el.closest( selector ) )
				.filter( Boolean );
			return new DollarCollection( [ ...new Set( found ) ], this );
		}

		children( selector ) {
			let found = this.elements.flatMap( ( el ) => Array.from( el.children ) );
			if ( selector ) {
				found = found.filter( ( el ) => el.matches( selector ) );
			}
			return new DollarCollection( found, this );
		}

		parent() {
			const found = this.elements.map( ( el ) => el.parentElement ).filter( Boolean );
			return new DollarCollection( [ ...new Set( found ) ], this );
		}

		prev() {
			const found = this.elements.map( ( el ) => el.previousElementSibling ).filter( Boolean );
			return new DollarCollection( [ ...new Set( found ) ], this );
		}

		siblings() {
			const found = this.elements.flatMap( ( el ) => Array.from( el.parentElement?.children || [] ).filter( ( sibling ) => sibling !== el ) );
			return new DollarCollection( [ ...new Set( found ) ], this );
		}

		end() {
			return this.prevObject || this;
		}

		is( selector ) {
			const first = this.elements[ 0 ];
			return !!first && first.matches( selector );
		}

		data( key, value ) {
			const first = this.elements[ 0 ];
			if ( !first ) {
				return typeof value === 'undefined' ? undefined : this;
			}

			if ( typeof value === 'undefined' ) {
				const dsKey = toDatasetKey( key );
				if ( typeof first.dataset?.[ dsKey ] !== 'undefined' ) {
					return first.dataset[ dsKey ];
				}
				const store = first[ INTERNAL_DATA_KEY ] || {};
				return store[ key ];
			}

			this.elements.forEach( ( el ) => {
				if ( !el[ INTERNAL_DATA_KEY ] ) {
					el[ INTERNAL_DATA_KEY ] = {};
				}
				el[ INTERNAL_DATA_KEY ][ key ] = value;
			} );
			return this;
		}

		removeData( keys ) {
			const normalized = Array.isArray( keys ) ? keys : [ keys ];
			this.elements.forEach( ( el ) => {
				if ( !el[ INTERNAL_DATA_KEY ] ) {
					return;
				}
				normalized.forEach( ( key ) => {
					delete el[ INTERNAL_DATA_KEY ][ key ];
				} );
			} );
			return this;
		}

		on( events, selectorOrHandler, maybeHandler ) {
			const eventNames = events.split( /\s+/ ).filter( Boolean );
			const delegated = typeof selectorOrHandler === 'string';
			const selector = delegated ? selectorOrHandler : null;
			const handler = delegated ? maybeHandler : selectorOrHandler;

			this.elements.forEach( ( el ) => {
				eventNames.forEach( ( eventName ) => {
					el.addEventListener( eventName, ( nativeEvent ) => {
						if ( delegated ) {
							const matched = nativeEvent.target.closest( selector );
							if ( !matched || !el.contains( matched ) ) {
								return;
							}
							handler.call( matched, nativeEvent );
							return;
						}

						if ( nativeEvent instanceof CustomEvent ) {
							handler.call( el, nativeEvent, nativeEvent.detail );
						} else {
							handler.call( el, nativeEvent );
						}
					} );
				} );
			} );

			return this;
		}

		bind( eventName, handler ) {
			return this.on( eventName, handler );
		}

		trigger( eventName, detail ) {
			this.elements.forEach( ( el ) => {
				el.dispatchEvent( new CustomEvent( eventName, { detail, bubbles: true } ) );
			} );
			return this;
		}

		val( value ) {
			if ( typeof value === 'undefined' ) {
				return this.elements[ 0 ]?.value;
			}
			this.elements.forEach( ( el ) => {
				el.value = value;
			} );
			return this;
		}

		prop( name, value ) {
			if ( typeof value === 'undefined' ) {
				return this.elements[ 0 ]?.[ name ];
			}
			this.elements.forEach( ( el ) => {
				el[ name ] = value;
			} );
			return this;
		}

		attr( name, value ) {
			if ( typeof value === 'undefined' ) {
				return this.elements[ 0 ]?.getAttribute( name );
			}
			this.elements.forEach( ( el ) => el.setAttribute( name, value ) );
			return this;
		}

		text( value ) {
			if ( typeof value === 'undefined' ) {
				return this.elements[ 0 ]?.textContent ?? '';
			}
			this.elements.forEach( ( el ) => {
				el.textContent = value;
			} );
			return this;
		}

		html( value ) {
			if ( typeof value === 'undefined' ) {
				return this.elements[ 0 ]?.innerHTML ?? '';
			}
			this.elements.forEach( ( el ) => {
				el.innerHTML = value;
			} );
			return this;
		}

		addClass( className ) {
			this.elements.forEach( ( el ) => el.classList.add( className ) );
			return this;
		}

		removeClass( className ) {
			this.elements.forEach( ( el ) => el.classList.remove( className ) );
			return this;
		}
	}

	function $( selectorOrElement ) {
		if ( selectorOrElement instanceof DollarCollection ) {
			return selectorOrElement;
		}
		if ( selectorOrElement === document ) {
			const wrapped = new DollarCollection( [ document ] );
			wrapped.ready = ( callback ) => {
				if ( document.readyState === 'loading' ) {
					document.addEventListener( 'DOMContentLoaded', callback, { once: true } );
				} else {
					callback();
				}
			};
			return wrapped;
		}
		if ( typeof selectorOrElement === 'string' ) {
			return new DollarCollection( Array.from( document.querySelectorAll( selectorOrElement ) ) );
		}
		if ( selectorOrElement === window || selectorOrElement instanceof Element ) {
			return new DollarCollection( [ selectorOrElement ] );
		}
		if ( Array.isArray( selectorOrElement ) || selectorOrElement instanceof NodeList ) {
			return new DollarCollection( Array.from( selectorOrElement ) );
		}
		return new DollarCollection( [] );
	}

	/**
	 * Exclamation point is allowed in urls without any encoding:
	 * @see https://discourse.gohugo.io/t/exclamation-mark-in-url-in-front-matter/22002/6
	 */
	const PARAM_SEPARATOR = '!';
	const TAG_SUFFIX = '-Tag';

	const IS_POPPING_STATE = 'is-popping-state';
	const IS_APPLYING_REQ = 'is-applying-req'
	const IS_RESETTING = 'is-resetting'


	// function throttle(callback, threshold) {
	// 	var wait = false;
	// 	return function (event) {
	// 		if (!wait) {
	// 			callback(event);
	// 			wait = true;
	// 			setTimeout(function () {
	// 				wait = false;
	// 			}, threshold);
	// 		}
	// 	}
	// }

	function debounce( func, wait, immediate ) {
		var timeout;
		return function() {
			var context = this, args = arguments;
			var later = function() {
				timeout = null;
				if ( !immediate ) func.apply( context, args );
			};
			var callNow = immediate && !timeout;
			clearTimeout( timeout );
			timeout = setTimeout( later, wait );
			if ( callNow ) func.apply( context, args );
		};
	}

	$( document ).ready( function() {

		$( '.filter-list' ).each( function( i, fl ) {

			const $filterList = $( fl );
			const $listing = $filterList.closest( '.listing-container' );
			const listingId = $listing.data('id');
			const $filterPanels = $filterList.find( '.filter-panel' );
			const $searchInput = $filterList.find( 'input[type="search"]' );

			setTimeout( init, 1 );

			function init() {

				$filterList.data( IS_RESETTING, true );

				/**
				 * RESET FILTERS
				 */
				$listing.find( '.js-reset-filters' ).on( 'click', (e) => {
					e.preventDefault();
					$filterList.data( IS_RESETTING, true )
					// $filterList.find( '.dropdown' ).each( ( idx, ctrl ) => {
					// 	const $ctrl = $(ctrl);
					// 	$ctrl.find('.dropdown-item.active').removeClass('active');
					// 	$ctrl.find('.dropdown-toggle').text( $ctrl.find('.dropdown-item:first-child').text() );
					// });
					// //todo reset rangesliders
					// $filterList.find( 'input:checked' ).click();
					$filterList.find( 'input[type="text"],input[type="search"],input[type="date"]' ).val( '' );
					// $filterList.find( 'input[type="search"]' ).val( '' ).parent().next().click();
					triggerFilter( { ['reset-filters'] : true } );
				} );

				/**
				 * SEARCH PHRASE
				 */
				$filterList.on( 'click', '.filter-panel--' + Facet.search_phrase + ' button', e => {
					// console.log( 'search button clicked', $( e.currentTarget ).prev().children().first() );
					doSearch( $( e.currentTarget ).prev().children().first() );
				} );

				$searchInput.on( 'search', () => {
					// console.log('search event', $searchInput.val() );
					doSearch( $searchInput );
				});

				// if ( $searchInput.val().trim() ) {
				// 	console.log('init search', $searchInput.val() );
				// 	doSearch( $searchInput );
				// }

				/**
				 * PROFESSION/STATE
				 */
				$filterList.find( '.filter-panel--' + Facet.state_code + ',.filter-panel--' + Facet.profession_id ).each( ( idx, ctrl ) => {
					$( ctrl ).on( 'click', '.dropdown-item:not(.disabled)', onDropdownClick );
				} );

				/**
				 * RANGE SLIDERS
				 */
				$filterList.find( '[data-range-slider]' ).each( ( idx, rs ) => {
					const $rs = $(rs);
					const facetName = $rs.closest('.filter-panel').data('name');
					const settings = JSON.parse( rs.getAttribute( 'data-settings' ) );

					if ( settings.range.max === -1 || settings.range.min[0] === settings.range.max[0] ) {
						$rs.closest('.filter-panel').parent().addClass('d-none');
					}

					if ( facetName === Facet.start_time ) {
						/**
						 * @see https://refreshless.com/nouislider/examples/#section-dates
						 */

						settings.tooltips = [ utils.dateFormatter( utils.timestampToDateVal( settings.range.min[0] )), utils.dateFormatter( utils.timestampToDateVal( settings.range.max[0] )) ];
					} else {
						settings.tooltips = [ utils.formatter( settings.step ), utils.formatter( settings.step ) ];
					}

					RangeSlider.create( rs, settings ).on( 'change', onRangeSliderUpdate );

				} );

				/**
				 * DATE CHANGE
				 */
				$filterList.find( 'input[type="date"]' ).on( 'change', onDateChange );

				/**
				 * CHECKBOX GROUPS, etc
				 */
				$filterList.find( 'select,textarea,input[type]:not([type="search"])' ).on( 'change', onValueChange );

				/**
				 * POP NAVIGATION STATE
				 */
				$( window ).on( 'popstate', e => onPopState( e.state ) );

				/**
				 * ITEMS CHANGED
				 *
				 * setTimeout is used to take it out to a separate thread and make the UI more responsive
				 */
				$listing.bind( Event.ITEMS_CHANGED, ( event, data ) => setTimeout( () => onItemsChanged( data ), 1 ) );
				$listing.get( 0 )?.addEventListener( Event.ITEMS_CHANGED, ( event ) => {
					setTimeout( () => onItemsChanged( event.detail || {} ), 1 );
				} );

				/**
				 * Apply the filters that are specified as query parameters
				 * @note, by the time we get here product lists should be already initialized
				 */
				setTimeout( applyRequestParams, 1 ); //fake yield to wait for appropriate event listeners to register themselves
			}

			/**
			 *
			 * @param popped
			 */
			function onPopState( popped ) {

				// console.log( 'popped', window.document.location, popped );

				/**
				 * we need this to avoid infinite loops
				 */
				$filterList.data( IS_POPPING_STATE, true );

				if ( popped && typeof popped.activeFilters !== 'undefined' ) {

					if ( popped.activeFilters[ Facet.search_phrase ] === null ){
						/**
						 * this is needed to force clearing search phrase
						 */
						popped.activeFilters[ Facet.search_phrase ] = null;
						popped.activeFilters[ Facet.search_phrase_item_ids ] = null;
					}

					triggerFilter( popped.activeFilters );
				}
			}

			/**
			 * @returns {boolean}
			 */
			function isPoppingState() {
				return !!$filterList.data( IS_POPPING_STATE );
			}

			/**
			 * @returns {boolean}
			 */
			function isApplyingReq() {
				return !!$filterList.data( IS_APPLYING_REQ );
			}

			/**
			 * @returns {boolean}
			 */
			function isResetting() {
				return !!$filterList.data( IS_RESETTING );
			}

			/**
			 * @returns {boolean}
			 */
			function isRegularUpdate(){
				//the update is most likely initiated by the user directly changing some filter
				return !isApplyingReq() && !isPoppingState() && !isResetting();
			}

			/**
			 * Collect and apply filters that are passed as request params.
			 */
			function applyRequestParams() {
				const reqParams = {}

				$filterPanels.each( function( idx, panel ) {
					const $panel = $( panel );
					const facetName = $panel.data( 'name' );
					/**
					 * @note facetNames in request url has "-Tag" as a suffix;
					 * facet names from any other source are prefixed with "tags::"
					 */
					const tagTypeName = getTagTypeName( facetName );
					const urlParams = new URLSearchParams( window.location.search );

					/**
					 * note, it is assumed that tagTypeName when used as a query param will have `-Tag` as a suffix
					 */
					const actualParamName = (tagTypeName === 'Delivery Method Override')
						? 'course-type'
						: tagTypeName
						  ? tagTypeName + TAG_SUFFIX
						  : facetName;

					let paramValues = urlParams.get( actualParamName );

					if ( null === paramValues ) {
						return true;
					}

					paramValues = paramValues.split( PARAM_SEPARATOR )
					paramValues = paramValues.filter( v => v ); //remove empty values

					if ( paramValues.length === 0 ) {
						return true;
					}

					paramValues = paramValues.map( ( val ) => decodeURI( val ) );

					if ( paramValues.length === 4 && Facet.location === facetName ){
						paramValues = {
							distance: +paramValues[ 0 ],
							zip: +paramValues[ 1 ],
							lat: +paramValues[ 2 ],
							lng: +paramValues[ 3 ],
						};
					} else {
						if ( paramValues.length === 2 && isRangeFacet( facetName ) ){
							paramValues = ( Facet.start_time === facetName )
							  ? {
									min: utils.dateValToTimestamp( paramValues[0] ),
									max: utils.dateValToTimestamp( paramValues[1] )
								}
							  : {
									min: paramValues[0],
									max: paramValues[1]
								}
						}
					}

					reqParams[ facetName ] = paramValues;

				} );

				if ( Object.entries( reqParams ).length ) {
					/**
					 * we found some filters in query parameters -- trigger a filtering event
					 * this will in turn trigger items_changed, and that's when we will update
					 * the controls to reflect the filters that got applied.
					 */
					$filterList.data( IS_APPLYING_REQ, true );

					if ( typeof reqParams[ Facet.search_phrase ] !== 'undefined' && reqParams[ Facet.search_phrase ] ){
						/**
						 * run ajax query first
						 */
						triggerFilter( reqParams );
					} else {
						triggerFilter( reqParams );
					}
				} else {
					/**
					 * Refresh anyway -- this is needed to refresh filters when some items are displayed outside of the Listings module, e.g. in the Product Slider
					 */
					setTimeout( function() { triggerFilter( {} ) }, 1000 ); //give it a second before running
				}
			}

			/**
			 * @param filters
			 */
			function triggerFilter( filters ) {
				fl.dispatchEvent( new CustomEvent( Event.FILTER_CHANGED, { detail: filters } ) );
				$filterList.trigger( Event.FILTER_CHANGED, filters );
			}

			/**
			 * @param $el
			 * @returns {boolean}
			 */
			function doSearch( $el ) {
				const $panel = $el.closest( '.filter-panel' );
				const $btn = $panel.find( 'button' );
				const searchPhrase = $el.val().trim();
				const facetName = $panel.data( 'name' );

				$el.prop( 'readonly', true );

				//onsole.log( facetName, searchPhrase, $btn );

				if ( searchPhrase ) {
					$btn.addClass( 'cds-glyph--spinner' );
					utils.runAjaxSearch(
						searchPhrase,
						window.colibri.data.matchingItemIds[listingId] ?? Object.keys( window.colibri.index.products ),
						r => {
						$el.prop( 'readonly', false );
						$btn.removeClass( 'cds-glyph--spinner' );

						triggerFilter( {
							[ facetName ]: searchPhrase,
							/**
							 * note, this comes from the backend; when there are no results it will be an empty array
							 */
							[ Facet.search_phrase_item_ids ]: r.data.matching_item_ids
						} );

					}, r => {
						$el.prop( 'readonly', false );
						$btn.removeClass( 'cds-glyph--spinner' );
					} );
				} else {
					triggerFilter( {
						[ Facet.search_phrase ]: null,
						[ Facet.search_phrase_item_ids ]: null
					} );
					$el.prop( 'readonly', false );
				}
				return true;
			}

			/**
			 * @param e
			 * @returns {boolean}
			 */
			function onValueChange( e ) {

				const $el = $( this );
				const $panel = $el.closest( '.filter-panel' );
				const facetName = $panel.data( 'name' );

				if ( Facet.start_time === facetName /* || e && typeof e.originalEvent === 'undefined' */ ) {
					return true;
				}

				if ( Facet.location === facetName ) {
					const $zip = $panel.find( '[name="cg_zip_code"]' );
					const $distance = $panel.find( '[name="cg_miles_from"]' );
					const distance = parseInt( $distance.val() );
					$distance.val( distance );

					if ( distance && $zip.val().length >= 5 ) {
						$zip.val( $zip.val().substr( 0, 5 ) );
						// API call to get (and cache) zip location
						// console.log('Requesting zip details', $zip.val() );
						fetch( window.colibri.studentApi + '/zipcode/' + encodeURI( $zip.val() ) )
							.then( ( response ) => {
								if ( !response.ok ) {
									throw response;
								}
								return response.json();
							} )
							.then( ( r ) => {
								triggerFilter( {
									[ facetName ]: {
										distance,
										zip: parseInt( $zip.val(), 10 ),
										lat: r.lat,
										lng: r.lng
									}
								} );
							} );
					} else {
						triggerFilter( {
							[ facetName ]: { distance: 0, zip: 0, lat: 0, lng: 0 }
						} );
					}
				} else if ( isRangeFacet( facetName ) ) {
					// console.log(facetName, 'onValueChange' );
					// triggerFilter( {
					// 	[ facetName ]: {
					// 		min: utils.maybeCastToInt( $panel.find( 'input' ).eq( 0 ).val() ),
					// 		max: utils.maybeCastToInt( $panel.find( 'input' ).eq( 1 ).val() )
					// 	}
					// } );
				} else {

					var values = [];
					if ( $el.attr( 'type' ) === 'radio' || $el.attr( 'type' ) === 'checkbox' ) {
						$panel.find( '.btn-group-toggle .btn input:checked' ).each( function( idx, it ) {
							// console.log( $( it ).val() );
							values.push( utils.maybeCastToInt( $( it ).val() ) );
						} );

						if ( !values.length ) {
							$panel.find( '[type="checkbox"]:checked:not(:disabled),[type="radio"]:checked:not(:disabled)' ).each( function( idx, it ) {
								const ids = it.getAttribute( 'data-ids' ); //$( it ).val();

								if ( ids !== null && typeof ids !== 'undefined' ) {
									if ( ids.indexOf( '|' ) > - 1 ) {
										values = values.concat( ids.split( '|' ) );
									} else {
										values.push( ids );
									}
								} else {
									values.push( utils.maybeCastToInt( $( it ).val() ) );
								}

							} );
						}

					} else {
						values.push( utils.maybeCastToInt( $el.val() ) );
					}

					triggerFilter( { [ facetName ]: values } );
				}
			}

			/**
			 *
			 * @param values
			 * @param handle
			 * @param unencoded
			 * @param tap
			 * @param positions
			 */
			function onRangeSliderUpdate( values, handle, unencoded, tap, positions ) {
				const $panel = $(this.target).closest('.filter-panel');
				const facetName = $panel.data('name');

				if ( !isNaN(values[0]) && !isNaN(values[1]) && Facet.start_time === facetName ) {
					$panel.find( '[name="cg_start_time_min"]' ).val( utils.timestampToDateVal( values[ 0 ] ) );
					$panel.find( '[name="cg_start_time_max"]' ).val( utils.timestampToDateVal( values[ 1 ] ) );
				} else {
					//console.log('Values are NaN');
				}

				triggerFilter( { [ facetName ]: { min: values[0], max: values[1] } } );
			}

			/**
			 * handle item select in State and Profession dropdowns
			 */
			function onDropdownClick( e ) {
				const $item = $( e.target );
				const $dd = $item.closest( '.dropdown' );
				const $panel = $dd.closest( '.filter-panel' );
				const ids = $item.data( 'ids' );

				$item.siblings().removeClass( 'active' ).end().addClass( 'active' );

				$dd.find( '.dropdown-toggle' ).text( $item.text() );

				triggerFilter( {
					[ $panel.data( 'name' ) ]: ids ? ( typeof ids === 'string' ) ? ids.split( '|' ) : [ ids ]: null
				} );

				return true;
			}

			/**
			 * @param e
			 */
			function onDateChange( e ) {
				const name = e.target.getAttribute( 'name' );

				if ( ['cg_start_time_min', 'cg_start_time_max'].indexOf( name ) > - 1 ) {

					let min = ( 'cg_start_time_min' === name )
								 ? e.target.value
								 : $listing.find( '[name="cg_start_time_min"]' ).val();

					let max = ( 'cg_start_time_max' === name )
								 ? e.target.value
								 : $listing.find( '[name="cg_start_time_max"]' ).val();

					min = utils.dateValToTimestamp( min, 'min' );
					max = utils.dateValToTimestamp( max, 'max' );

					// console.log(min, max);

					triggerFilter( { [ Facet.start_time ]: { min, max } } );
				}
			}

			function getNormalizedFilters( filters ) {
				Object.keys( Facet ).forEach( facetName => {
					if ( ( typeof ( filters[ facetName ] ) === 'undefined' ) /*|| ( Array.isArray( filters[ facetName ] ) && !filters[ facetName ].length )*/ ) {
						filters[ facetName ] = null;
					}
				} );
				return filters;
			}

			/**
			 * @param {object} activeFilters
			 * @param {array} prefilteredIds
			 * @returns {object}
			 */
			function getProductIdsNotFilteredBy( activeFilters, prefilteredIds ){

				const productIdsNotFilteredBy = {};

				Object.entries( activeFilters ).forEach(( [facetName, filterValues] ) => {

						if ( !isEnumerableFacet( facetName ) || !filterValues || !filterValues.length ){
							//skip facets that have no active filter
							return true; //continue
						}

						/**
						 * it is faster to perform intersection on sets than on arrays
						 */
						productIdsNotFilteredBy[ facetName ] = [ ...prefilteredIds ];

						// console.log(productIdsNotFilteredBy);

						Object.entries( activeFilters ).forEach( ( [ fn, fv ] ) => {

							if ( facetName === fn || !isEnumerableFacet( fn ) || !fv || !fv.length) {
								//skip current facet, non-enumerable facets and facets that have no active filter
								return true;
							}

							fv.forEach(
								v => {
									const pids = new Set( window.colibri.index.facets[ fn ][ v ] );
									productIdsNotFilteredBy[ facetName ] = productIdsNotFilteredBy[ facetName ].filter( pid => pids.has( pid ));
								}
							);
						});

						productIdsNotFilteredBy[ facetName ] = new Set( productIdsNotFilteredBy[ facetName ] );
					}
				);

				return productIdsNotFilteredBy;
			}

				/**
			 * Collect item ids from product lists that are currently relevant for this listing view.
			 * If multiple product lists exist, prefer visible ones; otherwise fall back to all.
			 *
			 * @returns {Set<number>}
			 */
			function getScopedCardIds() {
				const scopedIds = new Set();
				const $allProductLists = $listing.find( '.product-list-container' );

				if ( !$allProductLists.length ) {
					return scopedIds;
				}

				let $scopedProductLists = $allProductLists.filter( ( _, list ) => {
					const $list = $( list );
					return $list.is( ':visible' ) && $list.closest( '.d-none,[hidden]' ).length === 0;
				} );

				if ( !$scopedProductLists.length ) {
					$scopedProductLists = $allProductLists;
				}

				$scopedProductLists.find( '.card[data-item-id]' ).each( ( _, card ) => {
					const itemId = +card.getAttribute( 'data-item-id' );
					if ( itemId ) {
						scopedIds.add( itemId );
					}
				} );

				return scopedIds;
			}

			/**
			 * Hide filter options that do not apply to the current result set.
			 * Hide filter panels that have less than one available option.
			 *
			 * @param {object} data
			 */
			function onItemsChanged( data ) {

				let activeFilters = getNormalizedFilters( data['activeFilters'] );

				if ( !isPoppingState() && window.history.pushState ) {
					const pushUrl = utils.replaceUrlParams( window.location.href, activeFilters, PARAM_SEPARATOR, TAG_SUFFIX );
					window.history.pushState( { activeFilters }, null, pushUrl );
					if ( activeFilters[ Facet.delivery_method_id ] ?? null ) {
						setActiveAnchorbar( activeFilters[ Facet.delivery_method_id ] );
					}
				}

				const allMatchingIds = window.colibri.data.matchingItemIds[listingId] || [];
				const scopedCardIds = getScopedCardIds();
				const productIds = scopedCardIds.size
					? allMatchingIds.filter( pid => scopedCardIds.has( pid ) )
					: allMatchingIds;

				if ( !productIds.length ){
					$listing.find('.no-results').removeClass('d-none');
				} else {
					$listing.find('.no-results').addClass('d-none');
				}

				const productIdsSet = new Set( productIds );

				const allPrefilteredIds = window.colibri.data.prefilteredItemIds[listingId] || [];
				const prefilteredIds = scopedCardIds.size
					? allPrefilteredIds.filter( pid => scopedCardIds.has( pid ) )
					: allPrefilteredIds;
				// const prefilteredIdsSet = new Set( prefilteredIds );

				const productIdsNotFilteredBy = getProductIdsNotFilteredBy( data['activeFilters'], prefilteredIds );
				const products = productIds.map( pid => window.colibri.index.products[ pid ] );

				/**
				 * determine value ranges for products that got selected
				 */
				const min = Number.MAX_SAFE_INTEGER;
				const max = -1;
				const ranges = {
					[ Facet.primary_hours ]: { min, max },
					[ Facet.secondary_hours ]: { min, max },
					[ Facet.price ]: { min, max },
					[ Facet.rating ]: { min, max },
					[ Facet.start_time ]: { min, max },
				}

				products.forEach( product => {
					Object.keys( ranges ).forEach( facetName => {

						const productValue = product[ facetName ] ?? product['first_variant'][ facetName ] ?? null;

						if ( null === productValue ) {
							return true; //continue to the next product
						}

						if ( Facet.primary_hours === facetName || Facet.secondary_hours === facetName ) {
							/**
							 * these range fields contain arrays
							 */
							if ( 0 === productValue.length ) {
								return true;
							}

							productValue.forEach( val => {
								const value = parseFloat( val );
								if ( ranges[ facetName ].min > value ) {
									ranges[ facetName ].min = value
								}
								if ( ranges[ facetName ].max < value ) {
									ranges[ facetName ].max = value
								}
							} );

						} else {
							/**
							 * these range fields contain singular values
							 */
							const value = parseFloat( productValue );
							if ( ranges[ facetName ].min > value ) {
								ranges[ facetName ].min = value
							}
							if ( ranges[ facetName ].max < value ) {
								ranges[ facetName ].max = value
							}
						}

					} );
				} );

				$filterPanels.each( ( fidx, f ) => {
					const $panel = $( f );
					const facetName = $panel.data( 'name' );

					if ( [ Facet.search_phrase ].includes( facetName ) ) {
						return true;
					}

					const isFilteringByFacet = !utils.isEmptyFilterValue( activeFilters[ facetName ] );

					if ( Facet.location === facetName ){
						if ( isFilteringByFacet ) {
							$panel.find( '[name="cg_miles_from"]' ).val( activeFilters[ facetName ][ 'distance' ] );
							$panel.find( '[name="cg_zip_code"]' ).val( activeFilters[ facetName ][ 'zip' ] );
							/**
							 * Show location filter panel if some items have longitude specified
							 */
							$panel.removeClass( 'is-collapsed' );
							$panel.parent().removeClass('d-none');
						}
						return true;
					}

					const tagTypeName = getTagTypeName(facetName);

					if ( isRangeFacet( facetName ) ) {

						if ( !productIds.length && activeFilters[facetName] === null ) {
							/**
							 * no results and this filter is not active
							 */
							$panel.parent().addClass( 'd-none' )
						} else if (
							(ranges[ facetName ].max === - 1) && activeFilters[facetName] === null
						) {
							/**
							 * none of the currently selected items have this facet and filter is not active
							 */
							$panel.parent().addClass( 'd-none' );
						} else if ( ranges[ facetName ].min === ranges[ facetName ].max ){
							/**
							 * there is no variation in values, so there is no point in displaying the filter
							 */
							if ( !isFilteringByFacet ) {
								$panel.parent().addClass( 'd-none' );
							}
						} else {

							const $rangeSlider = $panel.find( '[data-range-slider]' );
							const rangeSliderEl = $rangeSlider.length ? $rangeSlider[ 0 ] : null;
							const sliderApi = rangeSliderEl && rangeSliderEl.noUiSlider;
							if ( sliderApi && typeof sliderApi.updateOptions === 'function' ) {
								sliderApi.updateOptions(
									{
										start: [
											activeFilters[ facetName ]
												? activeFilters[ facetName ].min
												: ((Facet.primary_hours === facetName || Facet.secondary_hours === facetName)
													? 0
													: ranges[ facetName ].min),
											activeFilters[ facetName ]
												? activeFilters[ facetName ].max
												: ranges[ facetName ].max,
										],
										// range: {
										// 	min: Math.min(activeFilters[ facetName ] ? activeFilters[ facetName ].min: 1000000000, ranges[ facetName ].min),
										// 	max: Math.max(activeFilters[ facetName ] ? activeFilters[ facetName ].max: -1, ranges[ facetName ].max)
										// },
									}
								);
							}

							if ( Facet.start_time === facetName ){
								const startTime = (data.activeFilters && data.activeFilters.start_time) ? data.activeFilters.start_time: null;
								$( '[name="cg_start_time_min"]' ).val( utils.timestampToDateVal( startTime ? Math.min( startTime.min, ranges.start_time.min ): ranges.start_time.min ) );
								$( '[name="cg_start_time_max"]' ).val( utils.timestampToDateVal( startTime ? Math.max( startTime.max, ranges.start_time.max ): ranges.start_time.max ) );
							}

							$panel.parent().removeClass( 'd-none' );

							if ( isFilteringByFacet ) {
								$panel.removeClass( 'is-collapsed' )
							}
						}
						return true;
					}

					const isSingular = isSingularFacet( facetName ); //is profession or state
					const filterValues = activeFilters[ facetName ];
					let countOptions = 0;

					if ( isSingular ) {
						$panel.find( '.dropdown-item' ).each( ( idx, ctrl ) => {
							const $ctrl = $( ctrl );
							const $input = $ctrl;
							const ctrlValues = $input.is( '[data-ids]' )
													 ? ( '' + $input.data( 'ids' ) ).split( '|' ).map( utils.maybeCastToInt )
													 : [$input.val()];
							const ctrlVal = ctrlValues.length ? ctrlValues[ 0 ]: null;

							if ( !ctrlVal ) {
								return true; //skipping the "All professions" option
							}

							const isFilteringByValue = isFilteringByFacet && (filterValues === ctrlVal || Array.isArray( filterValues ) && ( filterValues.includes( ctrlVal ) || filterValues.includes( '' + ctrlVal )));

							const currentIds = window.colibri.index.facets[ facetName ][ ctrlVal ];

							/**
							 * Count of matching products (calculated for every facet value)
							 */
							let countMatches;
							if ( facetName in productIdsNotFilteredBy && productIdsNotFilteredBy[ facetName ].size ) {
								countMatches = currentIds.filter( pid => productIdsNotFilteredBy[ facetName ].has( pid ) ).length;
							} else {
								countMatches = currentIds.filter( pid => productIdsSet.has( pid ) ).length;
							}

							/**
							 * Update visible labels
							 */
							const parts = $ctrl.text().split( ' (' );
							const label = parts[ 0 ] + ' (' + countMatches + ')';
							if ( isFilteringByValue ) {
								$input.parent().prev().text( label );
							}
							$input.text( label ).prop( 'disabled', !countMatches );

							if ( countMatches ) {
								countOptions ++;
							}

						} );
					} else {
						$panel.find( '.custom-checkbox, .btn-group-toggle .btn' ).each( ( idx, ctrl ) => {
							const $ctrl = $( ctrl );
							const $input = $ctrl.find( 'input' );
							const ctrlValues = $input.is( '[data-ids]' )
													 ? ( '' + $input.data( 'ids' ) ).split( '|' ).map( utils.maybeCastToInt )
													 : [$input.val()];

							const isFilteringByValue = isFilteringByFacet && ctrlValues.some( v => ( v === filterValues || ( '' + v ) === ( '' + filterValues ) || Array.isArray( filterValues ) && ( filterValues.includes( v ) || filterValues.includes( '' + v ) ) ) );

							const currentIds = window.colibri.index.facets[ facetName ];

							let matches = [];
							if ( facetName in productIdsNotFilteredBy && productIdsNotFilteredBy[ facetName ].size ) {
								ctrlValues.forEach( val => {
									matches = matches.concat( currentIds[ val ].filter( pid => productIdsNotFilteredBy[ facetName ].has( pid ) ) );
								} );
							} else {
								ctrlValues.forEach( val => {
									matches = matches.concat( currentIds[ val ].filter( pid => productIdsSet.has( pid ) ) );
								} );
							}
							matches = [...new Set( matches )]; //eliminate duplicates

							if ( matches.length || isFilteringByValue ) {
								const parts = $ctrl.text().split( ' (' );
								$ctrl.removeClass( 'd-none' ).find( 'label' ).text( parts[ 0 ] + ' (' + matches.length + ')' );
								if ( !isRegularUpdate() ) {
									$input.prop( 'checked', isFilteringByValue );
								}
								countOptions ++;
							} else {
								$ctrl.addClass( 'd-none' );
							}
						} );
					}

					if ( countOptions > ( ( !!tagTypeName || isSingular || facetName === Facet.delivery_method_id || facetName === Facet.credit_type || facetName === Facet.state_code ) ? 0 : 1 ) ) {
						if ( isFilteringByFacet ){
							/**
							 * Make sure a panel that has active filters gets expanded
							 */
							$panel.removeClass('is-collapsed')
						}

						/**
						 * Show filter panel when only one tag option is applicable.
						 * For all other types of filters, hide when less than two options are applicable.
						 */
						$panel.parent().removeClass( 'd-none' );

					} else {
						if ( isFilteringByFacet || facetName === Facet.state_code ) {
							$panel.removeClass('is-collapsed')
						} else {
							$panel.parent().addClass( 'd-none' );
						}
					}

				} );

				/**
				 * after all other panels are updated, check if ajax search still needs to be performed
				 */
				if  ( activeFilters[ Facet.search_phrase ] !== null ) {
					$searchInput.val( activeFilters[ Facet.search_phrase ] );
					if ( activeFilters[ Facet.search_phrase_item_ids ] === null ) {
						/**
						 * product ids have not been retrieved yet
						 */
						doSearch( $searchInput );
						return true;
					}
				} else {
					$searchInput.val('');
				}
				//TODO do this in a callback after doSearch completes
				$filterList.removeData( [ IS_POPPING_STATE, IS_APPLYING_REQ, IS_RESETTING ] );

			} // onItemsChanged()

			function setActiveAnchorbar (data) {
				$('.anchor-bar li.nav-item').removeClass('active')
				if (data && data.length > 0) {
					data.forEach(item => {
						let filterItem = $('[data-ids="'+item+'"]').data('anchor-target')
						$('[data-target-selector=".anchor-target-'+filterItem+'"]').parent().addClass('active')
					})
				}
			}

		} ); // $('.filter-list').each()

	} ); // $(document).ready()

} )();
